// Nuvia Design System - Retro Minimalist Theme

export const theme = {
  // Color Palettes
  colors: {
    dark: {
      // Background colors
      bg: {
        primary: '#0f0b1a',      // Deep purple-black
        secondary: '#1a1229',    // Dark purple
        tertiary: '#251a35',     // Medium dark purple
        elevated: '#2d1f42',     // Elevated surface
        overlay: 'rgba(15, 11, 26, 0.95)',
      },
      // Text colors
      text: {
        primary: '#e8e4f0',      // Off-white with purple tint
        secondary: '#b8b0cc',    // Muted purple-grey
        tertiary: '#8a7ea8',     // Dimmed purple-grey
        inverse: '#0f0b1a',      // Dark text for light backgrounds
      },
      // Accent colors
      accent: {
        primary: '#9d5cff',      // Vibrant purple
        secondary: '#7b42bc',    // Medium purple
        tertiary: '#5c3a8a',     // Deep purple
        hover: '#b47fff',        // Light purple hover
        active: '#8a4fff',       // Active state
      },
      // Semantic colors
      success: '#6dd47e',        // Soft green
      warning: '#ffb648',        // Warm orange
      error: '#ff6b8a',          // Soft red
      info: '#5eb3ff',           // Soft blue
      // Border colors
      border: {
        default: '#382b52',      // Subtle purple border
        focus: '#9d5cff',        // Accent border
        hover: '#4d3a6b',        // Hover border
      },
    },
    light: {
      // Background colors
      bg: {
        primary: '#faf8f5',      // Warm white
        secondary: '#f0ede8',    // Light beige
        tertiary: '#e6e1da',     // Medium beige
        elevated: '#ffffff',     // Pure white for cards
        overlay: 'rgba(250, 248, 245, 0.95)',
      },
      // Text colors
      text: {
        primary: '#1a1229',      // Dark purple-black
        secondary: '#4a3d5c',    // Medium dark purple
        tertiary: '#6e5f82',     // Muted purple
        inverse: '#faf8f5',      // Light text for dark backgrounds
      },
      // Accent colors
      accent: {
        primary: '#7b42bc',      // Rich purple
        secondary: '#9d5cff',    // Vibrant purple
        tertiary: '#5c3a8a',     // Deep purple
        hover: '#6835a3',        // Darker purple hover
        active: '#8a4fff',       // Active state
      },
      // Semantic colors
      success: '#3cb44b',        // Rich green
      warning: '#ff9f1c',        // Warm orange
      error: '#e63946',          // Rich red
      info: '#1982c4',           // Rich blue
      // Border colors
      border: {
        default: '#d4cec4',      // Subtle beige border
        focus: '#7b42bc',        // Accent border
        hover: '#c4bcb0',        // Hover border
      },
    },
  },

  // Typography
  typography: {
    fonts: {
      primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      mono: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
      heading: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    },
    sizes: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem',// 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
    },
    weights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeights: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Spacing (based on 8px grid)
  spacing: {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
  },

  // Border radius
  radius: {
    none: '0',
    sm: '0.25rem',   // 4px
    base: '0.5rem',  // 8px
    md: '0.75rem',   // 12px
    lg: '1rem',      // 16px
    xl: '1.5rem',    // 24px
    full: '9999px',
  },

  // Shadows (retro minimal - subtle)
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    glow: '0 0 20px rgba(157, 92, 255, 0.3)',
  },

  // Transitions
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '350ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Z-index layers
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
};

export default theme;
